// "Is someone else signing for [customer]?" (Session AUJ). The one optional question on the quote accept page
// and the same-tier renewal page, asked after the cover choice. Default "No, I'll sign": left alone it sends
// nothing at all, and the agreement goes to the quote's own contact exactly as before. "Yes" asks for the
// person's name and email; the agreement is then addressed to them for signature, and their welcome pack and
// Client Portal login follow once it is signed. The server is the authority on what it accepts (a strict shape,
// never an East London Elevators address, never an erased person's); this only stops an obviously incomplete
// answer from being sent. Plain English, no em dashes or double hyphens (house rule).
import { useId } from "react";

export interface SignatoryAnswer {
  someoneElse: boolean;
  name: string;
  email: string;
}

export const NO_SIGNATORY: SignatoryAnswer = { someoneElse: false, name: "", email: "" };

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// What the page sends: nothing when the customer is signing themselves, else the trimmed pair. Returns an error
// sentence when "Yes" is chosen but the answer is not complete enough to send.
export function signatoryToSend(a: SignatoryAnswer): { signatory?: { name: string; email: string }; error?: string } {
  if (!a.someoneElse) return {};
  const name = a.name.trim();
  const email = a.email.trim().toLowerCase();
  if (!name) return { error: "Please enter the name of the person signing." };
  if (!EMAIL_SHAPE.test(email)) return { error: "Please enter a valid email address for the person signing." };
  return { signatory: { name, email } };
}

export function SignatoryQuestion({
  customerName,
  value,
  onChange,
  disabled,
}: {
  customerName: string | null | undefined;
  value: SignatoryAnswer;
  onChange: (next: SignatoryAnswer) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const who = customerName?.trim() ? customerName.trim() : "your organisation";
  return (
    <fieldset className="cq-commence cq-signatory" disabled={disabled}>
      <legend>Is someone else signing for {who}?</legend>
      <div className="cq-signatory-choices" role="radiogroup">
        <label className="cq-signatory-choice">
          <input
            type="radio"
            name={`${id}-who`}
            checked={!value.someoneElse}
            onChange={() => onChange({ ...value, someoneElse: false })}
          />
          No, I&apos;ll sign
        </label>
        <label className="cq-signatory-choice">
          <input
            type="radio"
            name={`${id}-who`}
            checked={value.someoneElse}
            onChange={() => onChange({ ...value, someoneElse: true })}
          />
          Yes, someone else will sign
        </label>
      </div>
      {value.someoneElse && (
        <div className="cq-signatory-fields">
          <label htmlFor={`${id}-name`}>Their full name</label>
          <input
            id={`${id}-name`}
            type="text"
            autoComplete="name"
            maxLength={200}
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
          />
          <label htmlFor={`${id}-email`}>Their email address</label>
          <input
            id={`${id}-email`}
            type="email"
            autoComplete="email"
            maxLength={254}
            value={value.email}
            onChange={(e) => onChange({ ...value, email: e.target.value })}
          />
          <p className="cq-commence-note">
            We will send the agreement to this person to sign. Once it is signed, you and they will both receive
            the signed copy, and their welcome pack and online account login go to them.
          </p>
        </div>
      )}
    </fieldset>
  );
}
